'use client';

/**
 * Imperative height animation between intrinsic (`height: auto`) content sizes.
 *
 * Same pattern as modal bodies in `app-dialog.tsx`: outer wrapper gets a pixel
 * height from Motion; inner content stays naturally sized and is measured via
 * `ResizeObserver`. First paint uses `set()` (no tween); later changes use
 * `MOTION_POSITION_SMALL` (150 ms ease-position), matching dialog step swaps.
 *
 * Pass `layoutDependencies` when children swap without firing ResizeObserver
 * under test harnesses (jsdom's no-op observer) or when you want an explicit
 * remeasure after React state transitions.
 */
import { c as _c } from "react/compiler-runtime";
import { type AnimationPlaybackControls, animate, domAnimation, LazyMotion, m, useMotionValue, useReducedMotion, useTransform } from 'motion/react';
import type { ReactNode } from 'react';
import { useLayoutEffect, useRef } from 'react';
import { MOTION_POSITION_SMALL } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

/** Effect-deps primitives only — avoids `unknown` (FT-09). */
export type MotionAnimatedAutoHeightDependency = string | number | boolean | null | undefined;

/** Stable empty default so the spread into the layout effect's deps keeps a stable reference. */
const EMPTY_LAYOUT_DEPS: ReadonlyArray<MotionAnimatedAutoHeightDependency> = [];
export interface MotionAnimatedAutoHeightProps {
  children: ReactNode;
  /** Snap data hydration; enable only the surface that owns an intentional resize. */
  animateChanges?: boolean;
  /** Reserve a newly nonempty section before fading in its whole content. */
  revealAfterExpand?: boolean;
  /** Optional classes on the animated outer shell (`overflow: hidden` is always applied via style). */
  className?: string;
  /** Classes on the measured inner box (the element observed by ResizeObserver). */
  innerClassName?: string | undefined;
  /** Remeasure after layout when any of these change (same-frame commit via `useLayoutEffect`). */
  layoutDependencies?: ReadonlyArray<MotionAnimatedAutoHeightDependency>;
}
export function MotionAnimatedAutoHeight(t0) {
  const $ = _c(27);
  const {
    children,
    animateChanges: t1,
    revealAfterExpand: t2,
    className,
    innerClassName,
    layoutDependencies: t3
  } = t0;
  const animateChanges = t1 === undefined ? true : t1;
  const revealAfterExpand = t2 === undefined ? false : t2;
  const layoutDependencies = t3 === undefined ? EMPTY_LAYOUT_DEPS : t3;
  const innerRef = useRef(null);
  const reducedMotion = useReducedMotion();
  const height = useMotionValue(-1);
  const animation = useRef(null);
  const opacity = useMotionValue(1);
  const revealAnimation = useRef(null);
  const awaitingReveal = useRef(false);
  const targetHeight = useRef(-1);
  let t4;
  if ($[0] !== animateChanges || $[1] !== height || $[2] !== opacity || $[3] !== reducedMotion || $[4] !== revealAfterExpand) {
    t4 = () => {
      const node = innerRef.current;
      if (node === null) {
        return;
      }
      const commit = function commit(next) {
        if (targetHeight.current === next && animateChanges && reducedMotion !== true) {
          return;
        }
        animation.current?.stop();
        animation.current = null;
        revealAnimation.current?.stop();
        revealAnimation.current = null;
        const previousTarget = targetHeight.current;
        targetHeight.current = next;
        if (height.get() < 0 || !animateChanges || reducedMotion === true) {
          height.set(next);
          opacity.set(revealAfterExpand && next === 0 ? 0 : 1);
          awaitingReveal.current = false;
          return;
        }
        if (revealAfterExpand && (previousTarget === 0 || height.get() === 0) && next > 0) {
          awaitingReveal.current = true;
        }
        if (next === 0) {
          awaitingReveal.current = false;
        }
        opacity.set(revealAfterExpand && (next === 0 || awaitingReveal.current) ? 0 : 1);
        animation.current = animate(height, next, {
          ...MOTION_POSITION_SMALL,
          onComplete: () => {
            if (!awaitingReveal.current) {
              return;
            }
            awaitingReveal.current = false;
            revealAnimation.current = animate(opacity, 1, MOTION_POSITION_SMALL);
          }
        });
      };
      commit(node.offsetHeight);
      const ro = new ResizeObserver(() => commit(node.offsetHeight));
      ro.observe(node, {
        box: "border-box"
      });
      return () => {
        ro.disconnect();
        animation.current?.stop();
        animation.current = null;
        revealAnimation.current?.stop();
        revealAnimation.current = null;
        targetHeight.current = -1;
      };
    };
    $[0] = animateChanges;
    $[1] = height;
    $[2] = opacity;
    $[3] = reducedMotion;
    $[4] = revealAfterExpand;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== animateChanges || $[7] !== height || $[8] !== layoutDependencies || $[9] !== opacity || $[10] !== reducedMotion || $[11] !== revealAfterExpand) {
    t5 = [height, opacity, reducedMotion, animateChanges, revealAfterExpand, ...layoutDependencies];
    $[6] = animateChanges;
    $[7] = height;
    $[8] = layoutDependencies;
    $[9] = opacity;
    $[10] = reducedMotion;
    $[11] = revealAfterExpand;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useLayoutEffect(t4, t5);
  const renderedHeight = useTransform(height, _temp);
  let t6;
  if ($[13] !== className) {
    t6 = cn(className);
    $[13] = className;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = animateChanges && reducedMotion !== true ? renderedHeight : "auto";
  let t8;
  if ($[15] !== t7) {
    t8 = {
      height: t7,
      overflowX: "visible",
      overflowY: "clip"
    };
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = revealAfterExpand && reducedMotion !== true ? opacity : 1;
  let t10;
  if ($[17] !== t9) {
    t10 = {
      opacity: t9
    };
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== children || $[20] !== innerClassName || $[21] !== t10) {
    t11 = <m.div ref={innerRef} className={innerClassName} style={t10}>{children}</m.div>;
    $[19] = children;
    $[20] = innerClassName;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t6 || $[25] !== t8) {
    t12 = <LazyMotion features={domAnimation}><m.div className={t6} style={t8}>{t11}</m.div></LazyMotion>;
    $[23] = t11;
    $[24] = t6;
    $[25] = t8;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}
function _temp(v) {
  return v < 0 ? "auto" : v;
}